<template>
  <div
    id="upsells-swiper"
    :class="bem()"
  >
    <Swiper
      v-if="props.upsells"
      v-bind="swiperOptions"
      :class="bem('items', { 'no-loop': !swiperOptions.loop })"
    >
      <div :class="bem('swiper-controls')">
        <VisionIconButton
          icon="caret"
          background-colour="white"
          icon-colour="black"
          size="xx-small"
          :class="bem('swiper-controls-button', { left: true })"
        />
        <VisionIconButton
          icon="caret"
          background-colour="white"
          icon-colour="black"
          :rotate="180"
          size="xx-small"
          :class="bem('swiper-controls-button', { right: true })"
        />
      </div>
      <template v-for="(upsell, idx) in props.upsells">
        <SwiperSlide
          v-if="!isInCart(upsell)"
          :key="`${idx}:${upsell.sku || upsell.baseSKU}`"
          :class="[bem('item')]"
        >
          <component
            :is="getComponentType(upsell)"
            v-if="getComponentType"
            :key="upsell.sku || upsell.baseSKU"
            :upsell="upsell"
            :index="idx"
          />
        </SwiperSlide>
      </template>
    </Swiper>
  </div>
</template>

<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'

const bem = useBEM('UpsellItems')
const shopifyStore = useShopifyStore()

const props = defineProps({
  upsells: { type: Array, default: () => [] },
})

const modules = [Navigation]

const swiperOptions = ref({
  modules: modules,
  slidesPerView: 1.5,
  spaceBetween: 12,
  speed: 300,
  loop: true,
  navigation: {
    nextEl: `#upsells-swiper .UpsellItems__swiper-controls-button--right`,
    prevEl: `#upsells-swiper .UpsellItems__swiper-controls-button--left`,
  },
})

function getComponentType(upsellItem) {
  const { key, mode } = upsellItem

  switch (mode) {
    case 'lens_kit':
      return resolveComponent('UpsellItemLensKit')
    case 'capsule_case':
      return resolveComponent('UpsellItemCase')
    case 'compact_case':
      return resolveComponent('UpsellItemCase')
    case 'conversion_kit':
      return resolveComponent('UpsellItemConversionKit')
    case 'strap_kit':
      return resolveComponent('UpsellItemStrap')
    case 'lens_type':
      return resolveComponent('UpsellItemLensType')
    case 'simple_multiple':
      return resolveComponent('UpsellItemMultipleSimple')
    case 'simple':
      if (key === 'compactCase') return resolveComponent('UpsellItemCase')
      if (['microfibreKit'].includes(key))
        return resolveComponent('UpsellItemMicrofibre')
      return resolveComponent('UpsellItemSimple')
    default:
      return ''
  }
}

const availableUpsellItems = computed(() => {
  const upsoldSkus = shopifyStore.upsoldItems.map(
    (item) => item.summary.bundleBaseSku
  )
  return props.upsells.filter((item) => !upsoldSkus.includes(item.baseSKU))
})

function isInCart(upsellItem) {
  return !!shopifyStore.baseSkusInCart.find((item) => {
    if (item === upsellItem.baseSKU) {
      return true
    }
    return false
  })
}

watch(
  availableUpsellItems,
  (newVal) => {
    swiperOptions.value.loop = newVal.length > 3
  },
  { immediate: true }
)
</script>

<style lang="postcss">
.UpsellItems {
  @apply relative;

  @screen md {
    &:hover {
      .UpsellItems__swiper-controls-button {
        @apply translate-y-0;
      }
    }
  }

  &--see-more-btn-visible {
    .UpsellItems__items {
      @apply max-h-80 overflow-hidden relative
        md:max-h-none;
    }
  }

  &--is-showing-all {
    .UpsellItems__items {
      @apply max-h-none;

      &:after {
        @apply hidden;
      }
    }
  }
}

.UpsellItems__items {
  @apply pl-[var(--cart-inline-padding)] overflow-x-clip overflow-y-visible;

  &--no-loop {
    @apply pr-[var(--cart-inline-padding)];
  }
}

.UpsellItems__swiper-controls {
  @apply absolute top-0 left-0 pb-3
  w-full -translate-y-full pr-[var(--cart-inline-padding)]
  flex gap-2 justify-end overflow-clip;
}

.UpsellItems__swiper-controls-button {
  --vision-icon-button-bg-colour: theme(colors.grey-light);
  @apply bg-grey-light translate-y-[calc(100%_+_0.75rem)] transition-transform duration-300;

  &:last-child {
    @apply delay-100;
  }
}
</style>
